import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDate, formatDateTime, type GroupDto, type GroupSlideDto } from '@pb/shared';
import { ApiError } from '../api/client';
import { getGroup, groupFileUrl, retireGroup, setGroupSlides, unretireGroup, updateGroup } from '../api/groups';
import { searchSlides } from '../api/slides';
import { getWhereUsed } from '../api/whereUsed';
import { TypedConfirmDialog } from '../components/ConfirmDialogs';
import { Dialog } from '../components/Dialog';
import { HistoryList } from '../components/History';
import { Tabs } from '../components/Nav';
import { ReorderList } from '../components/ReorderList';
import { DataView, EmptyState, ErrorAlert, ErrorState, Loading } from '../components/States';
import { TagEditorDialog, TagList } from '../components/Tags';
import { Thumb } from '../components/Thumb';
import { WhereUsedPanel } from '../components/WhereUsed';
import { useDetailCrumb, useProject, useProjectOwnerGuard } from '../lib/project';
import { sameIds } from '../lib/reorder';
import { anyPending } from '../lib/thumbs';
import { pluralize } from '../lib/text';
import { useDebounced, usePolling, useResource } from '../lib/useResource';
import { NameDescriptionDialog } from './GroupsPage';
import { InlineTitle } from './SlidePage';

function SlidePickerDialog({
  exclude,
  onClose,
  onAdd,
}: {
  exclude: number[];
  onClose: () => void;
  onAdd: (slides: GroupSlideDto[]) => void;
}) {
  const { project } = useProject();
  const [text, setText] = useState('');
  const query = useDebounced(text, 300);
  const [chosen, setChosen] = useState<GroupSlideDto[]>([]);
  const results = useResource(() => searchSlides(project.id, { query, sort: 'title' }), [project.id, query]);
  const list = (results.data ?? []).filter((s) => !exclude.includes(s.id));

  return (
    <Dialog
      title="Add slides"
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={chosen.length === 0}
            onClick={() => onAdd(chosen)}
          >
            Add {chosen.length ? pluralize(chosen.length, 'slide') : 'slides'}
          </button>
        </>
      }
    >
      <input
        type="search"
        className="input"
        placeholder="Search titles and page text"
        aria-label="Search slides"
        value={text}
        data-autofocus
        onChange={(event) => setText(event.target.value)}
      />
      {results.error ? (
        <ErrorState error={results.error} onRetry={() => void results.reload()} />
      ) : results.data === undefined ? (
        <Loading />
      ) : list.length === 0 ? (
        <p className="muted">
          {query ? 'No slides match this search.' : 'Every slide of the library is already in this group, or the library is empty.'}
        </p>
      ) : (
        <div className="picker-list" role="group" aria-label="Slides">
          {list.map((s) => {
            const on = chosen.some((c) => c.slide_id === s.id);
            return (
              <label key={s.id} className="picker-item checkbox">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() =>
                    setChosen((c) =>
                      on
                        ? c.filter((x) => x.slide_id !== s.id)
                        : [
                            ...c,
                            {
                              slide_id: s.id,
                              title: s.title,
                              page_count: s.page_count,
                              retired: s.retired,
                              thumbnail: s.thumbnail,
                            },
                          ],
                    )
                  }
                />
                <Thumb thumb={s.thumbnail} alt={s.title} size="xs" />
                <span className="grow">{s.title}</span>
                <span className="small faint">{pluralize(s.page_count, 'page')}</span>
              </label>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}

function SlidesEditor({
  group,
  onReload,
  onSaved,
}: {
  group: GroupDto;
  onReload: () => Promise<void>;
  onSaved: (updated: GroupDto) => void;
}) {
  const { project } = useProject();
  const [draft, setDraft] = useState<GroupSlideDto[]>(group.slides);
  const [base, setBase] = useState(group.version_no);
  const [baseIds, setBaseIds] = useState<number[]>(group.slides.map((s) => s.slide_id));
  const [note, setNote] = useState('');
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [conflict, setConflict] = useState(false);
  const [saved, setSaved] = useState(false);

  const draftIds = draft.map((s) => s.slide_id);
  const dirty = !sameIds(draftIds, baseIds);

  useEffect(() => {
    const serverIds = group.slides.map((s) => s.slide_id);
    if (group.version_no === base) {
      const byId = new Map(group.slides.map((s) => [s.slide_id, s]));
      setDraft((d) => d.map((s) => byId.get(s.slide_id) ?? s));
      return;
    }
    if (!dirty) {
      setDraft(group.slides);
      setBase(group.version_no);
      setBaseIds(serverIds);
    }
  }, [group]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setConflict(false);
    setSaved(false);
    try {
      const updated = await setGroupSlides(group.id, base, draftIds, note);
      setNote('');
      setBaseIds(draftIds);
      setSaved(true);
      onSaved(updated);
    } catch (err) {
      if (err instanceof ApiError && err.reason === 'conflict') setConflict(true);
      else setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <div className="toolbar">
        <span className="small muted">
          {pluralize(draft.length, 'slide')} · {pluralize(draft.reduce((n, s) => n + s.page_count, 0), 'page')}
        </span>
        <span className="spacer" />
        <button type="button" className="btn btn-sm" onClick={() => setPicking(true)}>
          Add slides
        </button>
      </div>
      {draft.length === 0 ? (
        <EmptyState
          title="This group has no slides"
          action={
            <button type="button" className="btn btn-primary" onClick={() => setPicking(true)}>
              Add slides
            </button>
          }
        >
          Add slides from the library, then save to create a new version.
        </EmptyState>
      ) : (
        <ReorderList
          label="Slides in this group"
          items={draft}
          itemKey={(s) => String(s.slide_id)}
          itemLabel={(s) => s.title}
          onChange={setDraft}
          renderItem={(s, index) => (
            <div className="row">
              <span className="small faint" style={{ width: 24 }}>
                {index + 1}
              </span>
              <Thumb thumb={s.thumbnail} alt={s.title} size="sm" />
              <div className="stack grow" style={{ gap: 2 }}>
                <Link to={`/projects/${project.id}/slides/${s.slide_id}`}>{s.title}</Link>
                <div className="row-wrap">
                  <span className="xsmall faint">{pluralize(s.page_count, 'page')}</span>
                  {s.retired ? <span className="badge badge-warning">Retired</span> : null}
                </div>
              </div>
            </div>
          )}
          renderActions={(s) => (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setDraft((d) => d.filter((x) => x.slide_id !== s.slide_id))}
              aria-label={`Remove ${s.title} from the group`}
            >
              Remove
            </button>
          )}
        />
      )}
      {conflict ? (
        <div className="alert alert-warning row-between" role="alert">
          <span>Someone saved this group meanwhile. Reload to see the latest version; your unsaved changes are dropped.</span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => void onReload()}
          >
            Reload
          </button>
        </div>
      ) : null}
      {saved && !dirty ? (
        <div className="alert alert-success" role="status">
          Saved. The group is now at version {group.version_no}.
        </div>
      ) : null}
      <ErrorAlert error={error} />
      <div className="panel row" style={{ padding: 12 }}>
        <label htmlFor="group-note" className="small muted nowrap">
          Change note
        </label>
        <input
          id="group-note"
          className="input grow"
          value={note}
          maxLength={2000}
          placeholder="Optional"
          onChange={(event) => setNote(event.target.value)}
        />
        <button
          type="button"
          className="btn"
          onClick={() => {
            setDraft(group.slides);
            setBase(group.version_no);
            setBaseIds(group.slides.map((x) => x.slide_id));
          }}
          disabled={!dirty || busy}
        >
          Discard changes
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!dirty || busy}>
          {busy ? 'Saving…' : 'Save as new version'}
        </button>
      </div>
      {picking ? (
        <SlidePickerDialog
          exclude={draftIds}
          onClose={() => setPicking(false)}
          onAdd={(slides) => {
            setDraft((d) => [...d, ...slides]);
            setPicking(false);
          }}
        />
      ) : null}
    </div>
  );
}

function VersionsList({ group, onRetried }: { group: GroupDto; onRetried: () => void }) {
  const versions = [...group.versions].sort((a, b) => b.version_no - a.version_no);
  if (versions.length === 0) return <EmptyState title="No versions yet">Save the slide list to create version 1.</EmptyState>;
  return (
    <div className="stack">
      {versions.map((v) => (
        <div key={v.id} className="card" style={{ padding: 16 }}>
          <div className="row-wrap">
            <strong>Version {v.version_no}</strong>
            <span className="badge">{pluralize(v.slides.length, 'slide')}</span>
            <span className="small muted">
              {formatDateTime(v.created_at)}
              {v.author ? ` · ${v.author}` : ''}
            </span>
          </div>
          {v.note ? <p className="small pre" style={{ marginTop: 4 }}>{v.note}</p> : null}
          <div className="version-pages" style={{ marginTop: 12 }}>
            {v.slides.map((s) => (
              <div key={s.slide_id} className="stack" style={{ gap: 4, width: 140 }}>
                <Thumb thumb={s.thumbnail} alt={s.title} onRetried={onRetried} />
                <span className="xsmall" title={s.title} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.title}
                </span>
              </div>
            ))}
            {v.slides.length === 0 ? <span className="small faint">No slides in this version.</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

type GroupTab = 'slides' | 'versions' | 'history';

export function GroupPage() {
  const { groupId } = useParams();
  const id = Number(groupId);
  const group = useResource(() => getGroup(id), [id]);
  const whereUsed = useResource(() => getWhereUsed('group', id), [id]);
  const mismatched = useProjectOwnerGuard(group.data?.project_id);
  const [tab, setTab] = useState<GroupTab>('slides');
  const [dialog, setDialog] = useState<'details' | 'tags' | 'retire' | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [editorKey, setEditorKey] = useState(0);
  useDetailCrumb(group.data?.name);

  const pending =
    anyPending((group.data?.slides ?? []).map((s) => s.thumbnail)) ||
    anyPending((group.data?.versions ?? []).flatMap((v) => v.slides.map((s) => s.thumbnail)));
  usePolling(pending, () => void group.refresh());

  const unretire = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await unretireGroup(id);
      group.setData(updated);
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DataView resource={group} what="group" loadingLabel="Loading group…">
      {(g) => {
        if (mismatched) return <Loading label="Loading group…" />;
        return (
        <div className="section">
          <div className="page-header">
            <div className="page-title">
              <InlineTitle
                value={g.name}
                label="group"
                onSave={async (name) => {
                  await updateGroup(g.id, { name });
                  await group.refresh();
                }}
              />
              <div className="row-wrap">
                {g.retired ? <span className="badge badge-warning">Retired</span> : null}
                <span className="badge">Version {g.version_no}</span>
                <span className="badge">{pluralize(g.slide_count, 'slide')}</span>
                <span className="badge">{pluralize(g.page_count, 'page')}</span>
                <span className="small faint">Last change {formatDate(g.updated_at)}</span>
              </div>
              {g.description ? <p className="muted pre">{g.description}</p> : null}
              <div className="row-wrap">
                <TagList tags={g.tags} />
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setDialog('tags')}>
                  {g.tags.length ? 'Edit tags' : 'Add tags'}
                </button>
              </div>
            </div>
            <div className="page-actions">
              <button type="button" className="btn" onClick={() => setDialog('details')}>
                Edit details
              </button>
              <a className="btn" href={groupFileUrl(g.id)} download>
                Download group
              </a>
              {g.retired ? (
                <button type="button" className="btn" onClick={() => void unretire()} disabled={busy}>
                  Unretire
                </button>
              ) : (
                <button type="button" className="btn" onClick={() => setDialog('retire')}>
                  Retire
                </button>
              )}
            </div>
          </div>
          <ErrorAlert error={actionError} />
          <div className="two-col">
            <div className="stack">
              <Tabs
                label="Group views"
                active={tab}
                onChange={setTab}
                tabs={[
                  { key: 'slides', label: 'Slides' },
                  { key: 'versions', label: `Versions (${g.versions.length})` },
                  { key: 'history', label: 'History' },
                ]}
              />
              <div hidden={tab !== 'slides'}>
                <SlidesEditor
                  key={editorKey}
                  group={g}
                  onReload={async () => {
                    await group.refresh();
                    setEditorKey((k) => k + 1);
                  }}
                  onSaved={(updated) => {
                    group.setData(updated);
                    void whereUsed.refresh();
                  }}
                />
              </div>
              {tab === 'versions' ? <VersionsList group={g} onRetried={() => void group.refresh()} /> : null}
              {tab === 'history' ? <HistoryList target="group" id={g.id} /> : null}
            </div>
            <aside className="sidebar">
              <WhereUsedPanel target="group" id={g.id} resource={whereUsed} />
            </aside>
          </div>
          {dialog === 'details' ? (
            <NameDescriptionDialog
              title="Edit group details"
              submitLabel="Save"
              initialName={g.name}
              initialDescription={g.description}
              onClose={() => setDialog(null)}
              onSubmit={async (name, description) => {
                await updateGroup(g.id, { name, description });
                setDialog(null);
                await group.refresh();
              }}
            />
          ) : null}
          {dialog === 'tags' ? (
            <TagEditorDialog
              target="group"
              ids={[g.id]}
              initial={g.tags.map((t) => t.id)}
              title="Tags of this group"
              onClose={() => setDialog(null)}
              onSaved={() => {
                setDialog(null);
                void group.refresh();
              }}
            />
          ) : null}
          {dialog === 'retire' ? (
            <TypedConfirmDialog
              title="Retire group"
              name={g.name}
              what="group name"
              actionLabel="Retire group"
              busyLabel="Retiring…"
              onClose={() => setDialog(null)}
              onConfirm={async (confirm) => {
                const updated = await retireGroup(g.id, confirm);
                group.setData(updated);
                setDialog(null);
              }}
            >
              <p>
                A retired group is hidden unless “Show retired” is on and can be unretired at any time. Retiring is
                refused while the group is used in a presentation that is not retired.
              </p>
            </TypedConfirmDialog>
          ) : null}
        </div>
        );
      }}
    </DataView>
  );
}
